import { MouseEvent, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';

export type LockLevel = 'label' | 'field' | 'option';

interface LockIndicatorProps {
  /** The locked item's name, shown as "{name} is locked". */
  name: string;
  level: LockLevel;
  /** Opens the Rules step, where the rule is explained. */
  onViewRules?: () => void;
}

/** The lock icon on a label, field or option a Google rule locks, with the reason in a popover. */
export default function LockIndicator({
  name,
  level,
  onViewRules,
}: LockIndicatorProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Field cards toggle open on a header click, so the lock keeps its clicks to itself.
  const stop = (event: MouseEvent) => event.stopPropagation();

  return (
    <Box component="span" onClick={stop} sx={{ display: 'inline-flex' }}>
      <IconButton
        size="small"
        aria-label={`${name} is locked`}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <LockOutlined />
      </IconButton>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: 305,
              p: 1.5,
              border: (theme) => `0.5px solid ${theme.palette.divider}`,
              borderRadius: '4px',
              boxShadow: '4px 6px 20px 6px rgba(0, 0, 0, 0.08)',
            },
          },
        }}
      >
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <InfoOutlined sx={{ fontSize: 20 }} />
            <Typography variant="body2" sx={{ letterSpacing: '0.17px' }}>
              {name} is locked
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            This {level} is associated with rule(s) that locks it from changes.
          </Typography>
          {onViewRules && (
            <Link
              component="button"
              type="button"
              variant="caption"
              underline="hover"
              sx={{ alignSelf: 'flex-start' }}
              onClick={() => {
                setAnchorEl(null);
                onViewRules();
              }}
            >
              View Rules
            </Link>
          )}
        </Stack>
      </Popover>
    </Box>
  );
}
